'use client';

/**
 * Portfolio/Gallery Page
 * Complete gallery view of all project images
 */

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { portfolioProjects } from '@/data/portfolio';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from 'lucide-react';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  projectId: string;
  projectTitle: string;
  category: 'upholstery' | 'window-treatments';
}

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState<'all' | 'upholstery' | 'window-treatments'>('all');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  // Collect all images from all projects
  const allImages: GalleryImage[] = portfolioProjects.flatMap((project) => {
    const images: GalleryImage[] = [];

    // Add gallery images if available
    if (project.galleryImages && project.galleryImages.length > 0) {
      project.galleryImages.forEach((img) => {
        images.push({
          id: img.id,
          src: img.src,
          alt: img.alt,
          projectId: project.id,
          projectTitle: project.title,
          category: project.category,
        });
      });
    }

    return images;
  });

  // Filter images by category
  const filteredImages = allImages.filter((image) => {
    if (activeCategory === 'all') return true;
    return image.category === activeCategory;
  });

  const upholsteryCount = allImages.filter((i) => i.category === 'upholstery').length;
  const windowTreatmentsCount = allImages.filter((i) => i.category === 'window-treatments').length;

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-foreground to-foreground/90 text-background">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-yellow-400">
            Our Portfolio
          </h1>
          <p className="text-xl md:text-2xl text-background/90 max-w-3xl mx-auto leading-relaxed">
            Explore our complete collection of stunning project photography. Each image showcases
            our craftsmanship, attention to detail, and commitment to excellence.
          </p>
        </div>
      </section>

      {/* Filter and Gallery */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          {/* Category Filter */}
          <div className="flex justify-center mb-12">
            <Tabs defaultValue="all" className="w-full max-w-4xl" onValueChange={(value) => setActiveCategory(value as any)}>
              <TabsList className="grid w-full grid-cols-3 h-14">
                <TabsTrigger value="all" className="text-base">
                  All Images
                  <Badge variant="secondary" className="ml-2">
                    {allImages.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="upholstery" className="text-base">
                  Upholstery
                  <Badge variant="secondary" className="ml-2">
                    {upholsteryCount}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="window-treatments" className="text-base">
                  Window Treatments
                  <Badge variant="secondary" className="ml-2">
                    {windowTreatmentsCount}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Image Gallery Grid */}
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="relative aspect-[4/3] rounded-lg overflow-hidden cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-500"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg
                        className="w-12 h-12 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Project Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <p className="text-white text-sm font-semibold">{image.projectTitle}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">
                No images found in this category.
              </p>
            </div>
          )}

          {/* Lightbox Modal */}
          {selectedImage && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
              onClick={() => setSelectedImage(null)}
            >
              <div
                className="relative max-w-4xl w-full aspect-[4/3] rounded-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <Image
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  fill
                  className="object-contain"
                  sizes="100vw"
                />

                {/* Close Button */}
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 bg-background/90 hover:bg-background text-foreground p-2 rounded-full transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                {/* Image Info */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-4 backdrop-blur">
                  <p className="text-lg font-semibold">{selectedImage.projectTitle}</p>
                  <p className="text-sm text-white/80 mt-1">{selectedImage.alt}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Your Project Could Be Next
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90">
            Let us create a transformation you'll love. Every project receives the same
            meticulous attention to detail and commitment to excellence.
          </p>
          <Button
            size="lg"
            className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 text-lg px-8 py-6"
            asChild
          >
            <Link href="/contact">
              Start Your Project Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
